import classNames from 'classnames';
import moment from 'moment';
import React, { useMemo } from 'react';

import { api } from '../../../../api';
import { Student } from '../../../../api/types/student';
import CheckTickIcon from '../../../../assets/images/icon-outline-check-tick.svg';
import MenuMeatballsIcon from '../../../../assets/images/icon-outline-menu-meatball.svg';
import DropdownMenu from '../../../../components/DropdownMenu/DropdownMenu';
import { SkeletonLoader } from '../../../../components/SkeletonLoader';
import useSubscriptionContext, { SUBSCRIPTION_TYPE } from '../useSubscriptionContext';
import { Details } from './Details';

interface ClueyPlusProps {
  selectedStudent: Student | null;
}

export const ClueyPlus: React.FC<ClueyPlusProps> = ({ selectedStudent }) => {
  const {
    clueyPlus: { isLoading: isLoadingClueyPlus, subscription, nextPayment, showSpecialOfferDialog },
    enrolments: {
      active: { enrolments: activeEnrolments, isLoading: isLoadingActiveEnrolments },
    },
  } = useSubscriptionContext();

  const { subscriptionType, subscriptionStatus, subscriptionPrice, subscriptionEndDate: endDate } = subscription ?? {};

  const { data: accountDetails } = api.account.details.useQuery();

  const isSubscriptionExpired = useMemo(() => {
    return subscriptionType === SUBSCRIPTION_TYPE.TRIAL && subscriptionStatus === 'Cancelled';
  }, [subscriptionStatus, subscriptionType]);

  const isSubscriptionCancelled = useMemo(() => {
    return subscriptionType === SUBSCRIPTION_TYPE.CLUEY_PLUS && subscriptionStatus === 'Cancelled';
  }, [subscriptionStatus, subscriptionType]);

  const detailsWidth = useMemo(() => {
    return subscriptionType === SUBSCRIPTION_TYPE.TRIAL ? 'w-1/3' : 'w-1/2';
  }, [subscriptionType]);

  const price = useMemo(() => {
    switch (subscriptionType) {
      case SUBSCRIPTION_TYPE.CLUEY_PLUS:
        return `$${subscriptionPrice}/month`;
      case SUBSCRIPTION_TYPE.TRIAL:
        return 'Free';
      case SUBSCRIPTION_TYPE.BUNDLED:
      default:
        return 'Free with tutoring';
    }
  }, [subscriptionType, subscriptionPrice]);

  const subscriptionDescription = useMemo(() => {
    switch (subscriptionType) {
      case SUBSCRIPTION_TYPE.BUNDLED:
        return [
          'Self-paced learning on your schedule',
          'Get feedback fast with personalised AI support',
          'Your always-on study partner for school success',
        ];
      case SUBSCRIPTION_TYPE.CLUEY_PLUS:
        return [
          'Smart study support, available anytime',
          'Expert learning content, aligned to the curriculum',
          'Build confidence with every step ',
        ];
      case SUBSCRIPTION_TYPE.TRIAL:
        return [
          'Instant help when feeling stuck',
          'Make learning less stressful, more rewarding',
          'Build confidence with every step',
        ];
    }
  }, [subscriptionType]);

  const subscriptionStatusText = useMemo(() => {
    if (isSubscriptionExpired) {
      return <p className="font-medium text-blood-orange-5">Expired</p>;
    }
    if (isSubscriptionCancelled) {
      return <p className="font-medium text-grey-4">Cancelled</p>;
    }
    return <p className="font-medium text-green-5">Active</p>;
  }, [isSubscriptionExpired, isSubscriptionCancelled]);

  const hasActiveEnrolments = useMemo(() => {
    if (!isLoadingActiveEnrolments && selectedStudent) {
      return activeEnrolments
        .filter((enrolment) => enrolment.studentSfid === selectedStudent?.studentId)
        .some((enrolment) => enrolment.sessions?.length > 0);
    }
    return false;
  }, [activeEnrolments, isLoadingActiveEnrolments, selectedStudent]);

  if (isLoadingActiveEnrolments || isLoadingClueyPlus || !subscription) {
    return (
      <div className="flex w-full flex-col gap-y-10 lg:sticky lg:top-24 lg:w-1/2 lg:self-start">
        <SkeletonLoader loading={isLoadingActiveEnrolments} className="h-4 w-32" />
        <SkeletonLoader loading={isLoadingActiveEnrolments} className="mt-6 h-12 w-full" />
        <SkeletonLoader loading={isLoadingActiveEnrolments} className="mt-4 h-32 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full flex-col gap-y-10 lg:sticky lg:top-24 lg:w-1/2 lg:self-start">
        <div
          className={classNames(
            'flex flex-row',
            subscriptionType === SUBSCRIPTION_TYPE.TRIAL ? 'justify-start' : 'justify-between'
          )}
        >
          <h2 className="merriweather text-2xl font-bold">Cluey+</h2>
          {subscriptionType === SUBSCRIPTION_TYPE.TRIAL && !isSubscriptionExpired && (
            <span className="ml-3 flex items-center rounded-full bg-green-3 px-2 py-1 text-center text-xs font-semibold">
              Free trial ends in XX days
            </span>
          )}
          {isSubscriptionExpired && (
            <span className="ml-3 flex items-center rounded-full bg-[#E4DED7] px-2 py-1 text-center text-xs font-semibold">
              Free trial ended
            </span>
          )}
          {subscriptionType === SUBSCRIPTION_TYPE.CLUEY_PLUS && !isSubscriptionCancelled && (
            <DropdownMenu
              items={[
                {
                  label: 'End Cluey+ subscription',
                  onClick: () => showSpecialOfferDialog(),
                  disabled: hasActiveEnrolments,
                  optionLabel: hasActiveEnrolments
                    ? 'Cancel your active tutoring enrolments first before ending Cluey+ subscription'
                    : undefined,
                },
              ]}
              icon={<img src={MenuMeatballsIcon} alt="menu icon" />}
              showIcon={false}
              buttonClassName="border-none !px-1 !h-5"
              itemClassName="!py-3 !px-5 rounded-lg"
              dropdownClassName="!min-w-[300px]"
            />
          )}
        </div>
        <div className="flex flex-row gap-x-2">
          {subscriptionType === SUBSCRIPTION_TYPE.CLUEY_PLUS && !isSubscriptionCancelled && (
            <div className={classNames(detailsWidth)}>
              <p className="text-sm">Next payment</p>
              <p className="font-medium">
                {moment(nextPayment?.paymentDate).tz(accountDetails?.timezone).format('DD MMM YYYY')}
              </p>
            </div>
          )}
          {subscriptionType === SUBSCRIPTION_TYPE.TRIAL && !isSubscriptionExpired && (
            <div className={classNames(detailsWidth)}>
              <p className="text-sm">Trial expires</p>
              <p className="font-medium">XX days</p>
            </div>
          )}
          {(isSubscriptionCancelled || isSubscriptionExpired) && (
            <div className={classNames(detailsWidth)}>
              <p className="text-sm">{isSubscriptionCancelled ? 'Last active' : 'Trial expires'}</p>
              <p className="font-medium">{moment(endDate).tz(accountDetails?.timezone).format('DD MMM YYYY')}</p>
            </div>
          )}
          {!isSubscriptionCancelled && (
            <div className={classNames(detailsWidth)}>
              <p className="text-sm">Price</p>
              <p className="font-medium">{price}</p>
            </div>
          )}
          <div className={classNames(detailsWidth)}>
            <p className="text-sm">Status</p>
            {subscriptionStatusText}
          </div>
        </div>
        <div className="flex flex-col gap-y-4">
          {subscriptionDescription.map((msg) => (
            <div className="flex flex-row gap-x-2">
              <img src={CheckTickIcon} alt="check tick icon" className="h-4 w-4" />
              <p className="text-sm">{msg}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full lg:w-1/2">{selectedStudent && <Details student={selectedStudent} />}</div>
    </>
  );
};
